"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import { AI_AGENT_ROLE } from "@/lib/ai-assistant";
import { cn } from "@/lib/utils";
import { GENTLE_IN_CLASS } from "../row-entrance";
import { BotAvatar } from "./bot-avatar";

/**
 * Typing indicator rendered as a real bubble — avatar + bubble with
 * three animated dots inside. Reads as "this sender is composing"
 * with the same visual weight as a regular operator message, so
 * it slots cleanly into the conversation flow.
 */
export function TypingBubble({
  actorName,
  actorKind,
  actorAvatarUrl,
  actorPhase,
  aiAssistantName,
  aiAssistantAvatarUrl,
  organizationLogoUrl,
  organizationName,
}: {
  actorName: string;
  actorKind?: string;
  /** Resolved AI agent name — used instead of the raw actor name for AI/bot
   *  turns (null/empty when the agent identity is disabled → "AI Agent"). */
  aiAssistantName?: string | null;
  /** Operator's own photo from the SSE beat (`from.avatar`). Wins over
   *  the initials monogram / org logo for human senders; AI/bot beats
   *  ignore it (they use `aiAssistantAvatarUrl`). */
  actorAvatarUrl?: string;
  /** AI turn phase (`"thinking"` / `"searching"` / `"typing"`) — drives
   *  the caption beneath the bubble. Defaults to "typing" for operators. */
  actorPhase?: string;
  /** Operator-uploaded AI avatar — surfaced on AI/bot typing
   *  indicators when the configured `aiAssistantAvatarUrl` is set.
   *  When null, the indicator renders without an avatar (column
   *  collapses) — matches the no-bot-identity choice on the
   *  message-bubble side. */
  aiAssistantAvatarUrl?: string | null;
  organizationLogoUrl?: string | null;
  organizationName?: string;
}) {
  const t = useTranslations("messages");
  // Matches the message-bubble: an AI/bot avatar collapses when the operator
  // set none, so the dots + caption sit flush-left instead of leaving a gap.
  const isAiActor = actorKind === "ai" || actorKind === "bot";
  const showAvatar = !isAiActor || !!aiAssistantAvatarUrl;
  // AI/bot turns read as the configured agent name (or "AI Agent" when the
  // agent identity is disabled); operators keep their own name.
  const displayActorName = isAiActor ? aiAssistantName || AI_AGENT_ROLE : actorName;
  return (
    <motion.div
      layout="position"
      // Appears like a live message: llm-console gentle-in (CSS, mount only). Motion keeps the exit.
      initial={false}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className={cn("flex flex-col items-start gap-1.5", GENTLE_IN_CLASS)}
    >
      <div className="flex max-w-[85%] items-start gap-2">
        <BotAvatar
          size={28}
          senderKind={actorKind}
          // Threading the actor's name lets BotAvatar paint the
          // operator's initials in the monogram fallback when the
          // SSE typing event omits an avatar URL. Previously the
          // typing indicator showed the org's first letter "W"
          // regardless of who was typing.
          senderName={actorName}
          senderAvatarUrl={actorAvatarUrl}
          aiAssistantAvatarUrl={aiAssistantAvatarUrl}
          organizationLogoUrl={organizationLogoUrl}
          organizationName={organizationName}
          className="mt-0.5"
        />
        <div className="rounded-wx-lg bg-wx-bg-elevated px-4 py-3 text-sm text-wx-fg">
          <span className="inline-flex items-end gap-1">
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-wx-fg-muted [animation-delay:-0.3s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-wx-fg-muted [animation-delay:-0.15s]" />
            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-wx-fg-muted" />
          </span>
        </div>
      </div>
      <p className={cn("text-[11px] text-wx-fg-subtle", showAvatar ? "ml-9" : "ml-1")}>
        {/* Fall back to a generic name when the SSE event lacks a real
            one — the previous `"…"` placeholder read as a literal
            ellipsis next to "typing…", which looked broken. */}
        <span className="font-medium text-wx-fg-muted">
          {displayActorName && displayActorName !== "…" ? displayActorName : t("typingSomeone")}
        </span>
        <span aria-hidden="true"> · </span>
        <span>
          {actorPhase === "thinking"
            ? t("aiThinking")
            : actorPhase === "searching"
              ? t("aiSearching")
              : t("typing")}
        </span>
      </p>
    </motion.div>
  );
}
